import * as angular from 'angular';
import { IControllerService, ILocationService, IScope } from 'angular';
import { getCognitoToken } from 'angular/utils/cognito';
import { USER_ROLES } from 'app/_constants/user-roles.constant';
import { NetworkPointResource } from 'app/_resources/network-point.resource';
import { AuthService } from 'app/_services/auth.service';
import { PrivilegeService } from 'app/_services/privilegeService';

const appBaseUrl = '/react';

/**
 * TODO migrate to angular 7+ router when every component is migrated to angular 7+
 */
function routes(
    $routeProvider: angular.route.IRouteProvider,
    USER_ROLES: USER_ROLES
) {
    let requireAuthentication = [
        'privilegeService',
        function (privilegeService: PrivilegeService) {
            return privilegeService.hasPrivilege();
        }
    ];

    let loginResult = [
        'authService',
        function (authService: AuthService) {
            return authService.userLoginPromise.promise.then(
                (res) => res,
                () => undefined
            );
        }
    ];

    let acerTradeAllowed = [
        'ExternalTrade',
        function (ExternalTrade) {
            return ExternalTrade.shipperCreateExternalTradeAllowed().$promise.then(
                (res) => !!res,
                () => false
            );
        }
    ];

    const secondaryProposalDetails = {
        template: `<pkp-secondary-proposal-details id="$resolve.id" 
                                                  authenticated-user="$resolve.authenticatedUser">
                           </pkp-secondary-proposal-details>`,
        resolve: {
            id: ($route: angular.route.IRouteService) =>
                $route.current.params.id,
            authenticatedUser: loginResult
        }
    };
    secondaryProposalDetails.resolve.id.$inject = ['$route'];

    // customize $routeProvider.when to always ask for an up-to-date user.
    // the big problem with the existing angular and monolith code is that
    // many endpoints don't return 401s when being called by an unauthenticated user,
    // but 204s or something else. to ensure a consistent behaviour, we always check the user.
    const originalWhen = $routeProvider.when;
    $routeProvider.when = function customWhen(path, route) {
        route.resolve || (route.resolve = {});
        angular.extend(route.resolve, {
            __refreshAuth: [
                '$rootScope',
                ($rootScope: any) => {
                    if (!$rootScope.checkAuth) return; // initially not set, which is fine as it is called automatically in the controller that sets it
                    return $rootScope.checkAuth(); // configured in angular/src/main/webapp/app/security/formular.ts
                }
            ]
        });
        return originalWhen.call($routeProvider, path, route);
    };

    $routeProvider
        .when('/start', {
            template: `<ng-include src="'app/tso-dashboard/tso-dashboard.html'" ng-if="$resolve.role === 'TSO_USER'"></ng-include>`,
            resolve: {
                authenticatedUser: loginResult,
                role: (authService: AuthService) =>
                    authService.userLoginPromise.promise
                        .then((res) => res.role)
                        .catch(() => '')
            },
            controller: async (
                authService: AuthService,
                $scope: IScope,
                $controller: IControllerService,
                $location: ILocationService
            ) => {
                const controllerByRole = {
                    SHIPPER_ADMIN: 'DashboardCtrl as dashboard',
                    SHIPPER_USER: 'DashboardCtrl as dashboard',
                    SSO_ADMIN: 'TsoDashboardCtrl as dashboard',
                    TSO_USER: 'TsoDashboardCtrl as dashboard'
                };

                const tokens = await getCognitoToken();

                try {
                    const loggedInUser =
                        await authService.userLoginPromise.promise;
                    if (
                        loggedInUser.role === 'SSO_ADMIN' ||
                        loggedInUser.role === 'SHIPPER_USER' ||
                        loggedInUser.role === 'SHIPPER_ADMIN'
                    ) {
                        $location.path(`${appBaseUrl}/dashboard`).replace();
                    } else {
                        $controller(controllerByRole[loggedInUser.role], {
                            $scope: $scope
                        });
                    }
                } catch (error) {
                    // some users are not known by the monolith, but might be logged in
                    // via cognito
                    if (tokens)
                        $location.path(`${appBaseUrl}/dashboard`).replace();
                    else $location.path(`${appBaseUrl}/`).replace(); // guest user
                }
            }
        })
        .when('/loading', {
            template: '<div>Loading...</div>'
        })
        .when('/initialize', {
            template: '<div>Loading...</div>'
        })
        .when('/notifications', {
            template:
                '<pkp-tso-notification-overview></pkp-tso-notification-overview>',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/activity', {
            template: require('app/activity/activities.html'),
            controller: 'ActivitiesCtrl',
            controllerAs: 'ActivitiesCtrl',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads/create/trade', {
            template: '<pkp-trade-upload-wizard></pkp-trade-upload-wizard>'
        })
        .when('/uploads/create/product', {
            template: `<pkp-product-upload-wizard wizard-name="Manual product upload">
                           </pkp-product-upload-wizard>`,
            resolve: {
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/uploads/edit/product/:rawDataId?', {
            template: `<pkp-product-upload-wizard wizard-name="Edit upload"
                                            raw-data-id="$resolve.rawDataId">
                           </pkp-product-upload-wizard>`,
            resolve: {
                rawDataId: ($route: angular.route.IRouteService) =>
                    $route.current.params.rawDataId,
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/uploads/create/portfolio-code', {
            template: `<pkp-portfolio-code-upload-wizard></pkp-portfolio-code-upload-wizard>`,
            resolve: {
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/uploads/create/balancing-group', {
            template: `<pkp-balancing-group-upload-wizard></pkp-balancing-group-upload-wizard>`,
            resolve: {
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/uploads/create/reverse-auction', {
            template: `<pkp-reverse-auction-upload-wizard></pkp-reverse-auction-upload-wizard>`,
            resolve: {
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/uploads/xml', {
            template: require('app/upload-xml/xmlupload.html'),
            controller: 'XmlUploadsCtrl',
            controllerAs: 'xmlUpload',
            resolve: {
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/uploads', {
            template: require('app/uploads/uploads.html'),
            controller: 'UploadsCtrl',
            controllerAs: 'uploads',
            resolve: {
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/shippers', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/shippers/organisations-and-users`)
                    .replace();
            }
        })
        .when('/settings', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/general-transport-settings`)
                    .replace();
            }
        })

        .when('/settings/auctions', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/operator-settings/auctions`
                    )
                    .replace();
            }
        })

        .when('/settings/general', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/general-transport-settings`)
                    .replace();
            }
        })
        .when('/settings/company-information', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/operator-settings/company-information`
                    )
                    .replace();
            }
        })
        .when('/settings/secondary-market', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/operator-settings/secondary-trading`
                    )
                    .replace();
            }
        })
        .when('/settings/surrender', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/operator-settings/surrender`
                    )
                    .replace();
            }
        })
        .when('/settings/credit-limits', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/operator-settings/credit-limits`
                    )
                    .replace();
            }
        })
        .when('/operator-settings/user-management', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/organisation/users`)
                    .replace();
            }
        })
        .when('/operator-settings/company-information', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/organisation/company-information`
                    )
                    .replace();
            }
        })
        .when('/operator-settings/general', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/general-transport-settings`)
                    .replace();
            }
        })
        .when('/operator-settings/auctions', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/settings/auctions`).replace();
            }
        })
        .when('/operator-settings/:activeSetting?', {
            template:
                '<pkp-operator-settings active-setting="$resolve.activeSetting"></pkp-operator-settings>',
            resolve: {
                activeSetting: ($route: angular.route.IRouteService) =>
                    $route.current.params.activeSetting,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(
                        USER_ROLES.TSO_USER,
                        USER_ROLES.SSO_ADMIN
                    )
            }
        })
        .when('/shippers/details/:customerRefId/shipper-info', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/shippers/details/${$routeParams.customerRefId}`
                    )
                    .replace();
            }
        })
        .when('/shippers/details/:customerRefId/contact-details', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/shippers/${$routeParams.customerRefId}/contact-details`
                    )
                    .replace();
            }
        })
        .when('/shippers/details/:customerRefId', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/shippers/details/${$routeParams.customerRefId}`
                    )
                    .replace();
            }
        })
        .when('/shippers/details/:customerRefId/documents', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/shippers/${$routeParams.customerRefId}/documents`
                    )
                    .replace();
            }
        })
        .when('/shippers/details/:customerRefId/credit-limits', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/shippers/${$routeParams.customerRefId}/credit-limits`
                    )
                    .replace();
            }
        })
        .when('/credit-limits/:customerRefId', {
            template:
                '<pkp-shippers-details customer-ref-id="$resolve.customerRefId"></pkp-shippers-details>',
            resolve: {
                customerRefId: ($route: angular.route.IRouteService) =>
                    $route.current.params.customerRefId,
                access: requireAuthentication
            }
        })
        // this was used in Angular (singular "tso")
        .when('/market-information/players/tso/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/market-information/players/tsos/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        // looks like this was used in some emails (ISR-1819, plural "tsos")
        .when('/market-information/players/tsos/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/market-information/players/tsos/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/market-information/players/shipper', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-information/players/shipper`)
                    .replace();
            }
        })
        .when('/market-information/players/tsos', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-information/players/tsos`)
                    .replace();
            }
        })
        .when('/market-information/players', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-information/players/tsos`)
                    .replace();
            }
        })
        .when('/market-information/rates', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-information/rates/current`)
                    .replace();
            }
        })
        .when('/market-information/rates/current', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-information/rates/current`)
                    .replace();
            }
        })
        .when('/market-information/rates/archive', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-information/rates/archive`)
                    .replace();
            }
        })
        .when('/market-information/messages', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-messages/maintenance-messages`)
                    .replace();
            }
        })
        .when('/market-information/message', {
            template: '',
            controller($routeParams, $location, MarketsMessages) {
                MarketsMessages.details(
                    {
                        messageId: $routeParams.messageId
                    },
                    function (res) {
                        const params = new URLSearchParams();

                        params.append('status', '_null_');
                        params.append('eventPeriodFrom', res.publicationDate);
                        params.append('eventPeriodTo', res.publicationEnd);
                        params.append('operatorId[0][label]', res.tso.name);
                        params.append(
                            'operatorId[0][value]',
                            res.tso.organizationId
                        );
                        params.append(
                            'operatorId[0][shortName]',
                            res.tso.shortName
                        );

                        $location
                            .path(
                                `${appBaseUrl}/market-messages/maintenance-messages?${params.toString()}`
                            )
                            .replace();
                    },
                    function () {
                        $location
                            .path(
                                `${appBaseUrl}/market-messages/maintenance-messages`
                            )
                            .replace();
                    }
                );
            }
        })
        .when('/account-settings/premium-services', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/premium-services`)
                    .replace();
            }
        })
        .when('/account-settings/contacts', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/organisation/contacts`)
                    .replace();
            }
        })
        .when('/account-settings/company-information', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/shipper-settings/company-information`
                    )
                    .replace();
            }
        })
        .when(
            '/account-settings/assignment-of-tsos/contracting-services/:id?',
            {
                template: require('app/account-settings/contracting-services.html'),
                controller() {},
                resolve: {
                    access: requireAuthentication
                }
            }
        )
        .when('/platform-settings/general', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/general`
                    )
                    .replace();
            }
        })
        .when('/platform-settings/dashboard', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/dashboard`
                    )
                    .replace();
            }
        })
        .when('/platform-settings/network-points', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/network-points`
                    )
                    .replace();
            }
        })
        .when('/platform-settings/trader-lists', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/secondary-trading`
                    )
                    .replace();
            }
        })
        .when('/platform-settings/secondary-trading', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/secondary-trading`
                    )
                    .replace();
            }
        })
        .when('/platform-settings/new-trader-list', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/new-trader-list`
                    )
                    .replace();
            }
        })
        .when('/platform-settings/conditions', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/user-settings/secondary-trading`
                    )
                    .replace();
            }
        })
        .when('/user-settings/:page?/:subPage?/:id?', {
            template: require('app/platform-settings/platform-settings.html'),
            controller: 'PlatformSettingsCtrl',
            controllerAs: 'platSettings',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/shipper-settings/company-information', {
            template: '',
            controller($location) {
                $location
                    .path(`/settings/organisation/company-information`)
                    .replace();
            }
        })
        .when('/shipper-admin', {
            template: '',
            controller($location) {
                $location
                    .path(`/settings/organisation/company-information`)
                    .replace();
            }
        })
        .when('/shipper-admin/register', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/organisation/users`)
                    .replace();
            }
        })
        .when('/organisation/users', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/organisation/users`)
                    .replace();
            }
        })
        .when('/shipper-admin/register/confirmation', {
            template:
                '<pkp-shipper-admin-user-registration-confirmation-page>' +
                '</pkp-shipper-admin-user-registration-confirmation-page>'
        })
        .when('/shipper-admin/:userId', {
            template: '<pkp-shipper-admin-user></pkp-shipper-admin-user>',
            resolve: {
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SHIPPER_ADMIN)
            }
        })
        .when('/shipper-admin/details/:userId', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/settings/organisation/users`)
                    .replace();
            }
        })
        .when('/shipper-admin/details/:userId/edit-assignments', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/operators/assignments/overview`)
                    .replace();
            }
        })
        .when('/auctions', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/transport/auctions`).replace();
            }
        })
        .when('/auctions/list', {
            template: '',
            controller($location) {
                const rawSearch = location.hash.replace('#/auctions/list', '');
                const modifiedSearch = new URLSearchParams(rawSearch);
                modifiedSearch.delete('networkPoint');

                // Convert URLSearchParams to a plain object
                const paramsObject = {};
                modifiedSearch.forEach((value, key) => {
                    paramsObject[key] = value;
                });

                $location
                    .path(`${appBaseUrl}/transport/auctions/list`)
                    .search(paramsObject)
                    .replace();
            }
        })
        .when('/multi-year-auctions', {
            template: `<pkp-multi-year-capacity></pkp-multi-year-capacity>`
        })
        .when('/multi-year-auctions/:id', {
            template: `<pkp-multi-year-capacity-details [auction-id]="$resolve.id"></pkp-multi-year-capacity-details>`,
            resolve: {
                id: ($route) => $route.current.params.id
            }
        })
        .when('/auctions/reverse/:id?', {
            template: require('app/reverse-auction-bid/reverse-auction-bid.html'),
            controller: 'ReverseAuctionBidCtrl',
            controllerAs: 'reverseAuctionBid'
        })
        .when('/auctions/:auctionId/:bidOrDetails', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/auctions/${$routeParams.auctionId}`
                    )
                    .replace();
            }
        })
        .when('/auctions/:auctionId/bid/:orderId/edit/', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/auctions/${$routeParams.auctionId}/${$routeParams.orderId}`
                    )
                    .replace();
            }
        })
        .when('/secondary-overview/trades/details/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/secondary/trades/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/secondary-overview/proposals/details/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/secondary/proposals/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/secondary-trading/details/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/secondary/proposals/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/secondary-overview/:type', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/secondary/${$routeParams.type}`
                    )
                    .replace();
            }
        })
        .when('/secondary-overview', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/transport/secondary`).replace();
            }
        })
        .when('/secondary-trading', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/transport/secondary`).replace();
            }
        })
        .when('/create-proposal/:stage?', {
            template: require('app/create-proposal/create-proposal.html'),
            controller: 'CreateProposalCtrl',
            controllerAs: 'createProposal',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/create-external-trade/:stage?', {
            redirectTo: '/reporting/secondary/external-trades'
        })
        .when('/edit-proposal/:id?/:stage?', {
            template: require('app/create-proposal/create-proposal.html'),
            controller: 'CreateProposalCtrl',
            controllerAs: 'createProposal',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/comfort-bid/create', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/auctions/comfort-bids/create`
                    )
                    .replace();
            }
        })
        .when('/comfort-bid/edit/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/transport/auctions/comfort-bids/edit/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/surrender-capacity/:networkPointId', {
            template: require('app/capacity-surrender/surrender-capacity.html'),
            controller: 'CapacitySurrenderCtrl',
            controllerAs: 'surrenderCapacity',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/network-point', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/transport/network-points`)
                    .replace();
            }
        })
        .when('/network-point/details/:networkPointId', {
            template:
                '<pkp-network-point-details-page network-point-id="$resolve.id"></pkp-network-point-details-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.networkPointId,
                authenticatedUser: loginResult
            }
        })
        .when('/network-point/create/:networkPointId?', {
            template:
                '<pkp-network-point-details-page network-point-id="$resolve.id"></pkp-network-point-details-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.networkPointId,
                access: requireAuthentication,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/network-point/bundle', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/transport/bundle-requests`)
                    .replace();
            }
        })
        .when('/network-point/bundle/details/:id', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/create', {
            template:
                '<pkp-network-point-bundle-create-page network-point-for-creation="$resolve.networkPointForCreation"></pkp-network-point-bundle-create-page>',
            resolve: {
                networkPointForCreation: (
                    $route: angular.route.IRouteService,
                    NetworkPoint: NetworkPointResource
                ) =>
                    NetworkPoint.get({ id: $route.current.params.nwp })
                        .$promise,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/change/:id?', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/delete/:id?', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/conversation/:id?', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/reporting/secondary/external-trade/details/:tradeId', {
            template:
                '<pkp-external-trade-details trade-id="$resolve.tradeId" is-report="true"></pkp-external-trade-details>',
            resolve: {
                tradeId: ($route: angular.route.IRouteService) =>
                    $route.current.params.tradeId
            }
        })
        // this old URL seems to be used at least in capacity linking, maybe also older system, emails or bookmarks
        .when('/reporting/fcfs/details/:id', {
            template: '',
            controller($routeParams, Fcfs, $location) {
                Fcfs.getFcfsOrder(
                    { id: $routeParams.id },
                    (res) => {
                        // we cannot redirect directly to the new detail pages as we don't know the UUID here.
                        // but this should be good enough.
                        $location
                            .path(
                                `${appBaseUrl}/reporting/my-transactions?transactionType%5B0%5D=FCFS&transactionTypeSpecificId=${res.dealId}`
                            )
                            .replace();
                    },
                    () => {
                        $location.path(`${appBaseUrl}/error`).replace();
                    }
                );
            }
        })
        .when('/reporting/secondary/trades/details/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/reporting/secondary/trades/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/reporting/secondary/proposals/details/:id', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/reporting/secondary/proposals/${$routeParams.id}`
                    )
                    .replace();
            }
        })
        .when('/reporting/secondary/:type', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/reporting/secondary/${$routeParams.type}`
                    )
                    .replace();
            }
        })
        .when('/reporting/secondary', {
            redirectTo: '/reporting/secondary/trades'
        })
        .when('/reporting/upload/details/:auctionId', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/reporting/products/uploaded/${$routeParams.auctionId}`
                    )
                    .replace();
            }
        })
        .when('/reporting/auction/details/:auctionId', {
            template: '',
            controller($routeParams, $location) {
                $location
                    .path(
                        `${appBaseUrl}/reporting/auctions/details/${$routeParams.auctionId}`
                    )
                    .replace();
            }
        })
        .when('/reporting/:reportType/details/:auctionId', {
            template: require('app/reporting-details/parent-report-details.html'),
            controller: 'ReportDetailsCtrl',
            controllerAs: 'reportDetails'
        })
        .when('/reporting/upload', {
            template: '',
            controller($location) {
                const search = location.hash.replace('#/reporting/upload', '');
                $location
                    .path(`${appBaseUrl}/reporting/products/uploaded${search}`)
                    .replace();
            }
        })
        .when('/reporting/auction', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/reporting/auctions/short-and-long-term-auctions`
                    )
                    .replace();
            }
        })
        .when('/reporting/standard', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/reporting/standard`).replace();
            }
        })
        .when('/reporting/reverse', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/reporting/auctions/reverse-auctions`)
                    .replace();
            }
        })
        .when('/reporting/surrender', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/reporting/surrenders`).replace();
            }
        })
        .when('/reporting/:reportType?', {
            template: require('app/reporting/parent-report.html'),
            controller: 'ReportingCtrl',
            controllerAs: 'reporting'
        })
        .when('/storage/offers', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/storage/offers`).replace();
            }
        })
        .when('/storage/offers/create', {
            template:
                '<pkp-create-storage-offer authenticated-user="$resolve.authenticatedUser"></pkp-create-storage-offer>',
            resolve: {
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SSO_ADMIN)
            }
        })
        .when('/storage/settings/company-information', {
            template: '',
            controller($location) {
                $location
                    .path(`/settings/organisation/company-information`)
                    .replace();
            }
        })
        .when('/storage/settings', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/storage-settings/premium-services`
                    )
                    .replace();
            }
        })
        .when('/storage/settings/premium-services', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/storage-settings/premium-services`
                    )
                    .replace();
            }
        })
        .when('/storage/settings/advanced', {
            template: '',
            controller($location) {
                $location
                    .path(
                        `${appBaseUrl}/settings/platform/#/storage-settings/general`
                    )
                    .replace();
            }
        })
        .when('/storage-settings/:activeSetting?', {
            template:
                '<pkp-storage-settings active-setting="$resolve.activeSetting" authenticated-user="$resolve.authenticatedUser">' +
                '</pkp-storage-settings>',
            resolve: {
                activeSetting: ($route: angular.route.IRouteService) =>
                    $route.current.params.activeSetting,
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SSO_ADMIN)
            }
        })
        .when('/storage/offers/details/:offerId/:action?', {
            template:
                '<pkp-storage-offer-details offer-id="$resolve.offerId" logged-in-user="$resolve.loggedInUser"></pkp-storage-offer-details>',
            resolve: {
                loggedInUser: loginResult,
                offerId: ($route: angular.route.IRouteService) =>
                    $route.current.params.offerId
            }
        })
        .when('/storage/locations', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/storage/locations`).replace();
            }
        })
        .when('/storage/locations/details/:id', {
            template: `<pkp-storage-location-details-page storage-location-id="$resolve.id"></pkp-storage-location-details-page>`,
            resolve: {
                id: ($route: ng.route.IRouteService) =>
                    $route.current.params.id,
                authenticatedUser: loginResult
            }
        })
        .when('/storage/locations/edit/:id', {
            template: `<pkp-storage-location-details-page storage-location-id="$resolve.id"></pkp-storage-location-details-page>`,
            resolve: {
                id: ($route: ng.route.IRouteService) =>
                    $route.current.params.id,
                authenticatedUser: loginResult
            }
        })
        .when('/storage/locations/create', {
            template: `<pkp-storage-location-details-page></pkp-storage-location-details-page>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/storage/booking', {
            template: `<pkp-wizard-host wizard-name="Storage FCFS booking" 
                                            template-url="app/storage-booking-wizard/storage-booking-wizard.component.html">
                           </pkp-wizard-host>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/access-denied', {
            template: require('app/security/access-denied.html')
        })
        .when('/permission-denied', {
            template: require('app/security/permission-denied.html')
        })
        .when('/maintenance-messages', {
            template: '',
            controller($location) {
                $location
                    .path(`${appBaseUrl}/market-messages/maintenance-messages`)
                    .replace();
            }
        })
        .when('/tso-user', {
            template: '',
            controller($location) {
                $location.path(`${appBaseUrl}/settings/profile`).replace();
            }
        })
        .otherwise({ redirectTo: '/start' });
}

angular.module('app').config(routes);
